import React, { useState, useEffect, useCallback } from "react";
import { getMonthly, getHolidays } from "../../../services/attendanceApi";

/**
 * AttendanceCalendar — monthly calendar grid, color-coded by status.
 * Click a day → mini popup banner with that day's details.
 */

const STATUS_CLASS = {
  PRESENT: "atl-day--present",
  LATE: "atl-day--late",
  ABSENT: "atl-day--absent",
  HALF_DAY: "atl-day--half",
  WEEKEND: "atl-day--weekend",
  HOLIDAY: "atl-day--holiday",
  ON_LEAVE: "atl-day--leave",
  WFH: "atl-day--wfh",
};

// Short professional status codes shown inside each calendar day cell.
// Kept minimal (2-4 chars) so cells stay clean and uncrowded.
const STATUS_LABEL = {
  PRESENT: "P",
  LATE: "Late",
  ABSENT: "A",
  HALF_DAY: "½ Day",
  WEEKEND: "Off",
  HOLIDAY: "Hol",
  ON_LEAVE: "Leave",
  WFH: "WFH",
};

const MONTHS = ["January","February","March","April","May","June",
  "July","August","September","October","November","December"];

function AttendanceCalendar({ refreshKey, onPeriodChange }) {
  const now = new Date();
  const [month, setMonth] = useState(now.getMonth() + 1); // 1-12
  const [year, setYear] = useState(now.getFullYear());
  const [days, setDays] = useState({}); // { "YYYY-MM-DD": {status, checkIn, checkOut, hours} }
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [popup, setPopup] = useState(null); // { day, info }

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const res = await getMonthly(month, year);
      const data = res && res.data ? res.data : res;
      // Expecting an array of records or a keyed map — normalize to a map by date
      const map = {};
      const list = Array.isArray(data) ? data : (data.records || data.days || []);
      list.forEach((r) => {
        const key = r.workDate || r.work_date || r.date;
        if (key) map[key] = r;
      });

      // Holiday overlay — merge company holidays into the day map.
      // Be defensive: if the call fails, just skip the overlay.
      try {
        const hRes = await getHolidays(year);
        const hData = hRes && hRes.data ? hRes.data : hRes;
        const holidays = Array.isArray(hData) ? hData : (hData.holidays || []);
        holidays.forEach((h) => {
          const key = h.holiday_date || h.holidayDate || h.date;
          if (!key) return;
          // Only overlay holidays that fall inside the shown month.
          if (key.slice(0, 7) !== `${year}-${String(month).padStart(2, "0")}`) return;
          const isWorking = (h.is_working ?? h.isWorking) === true;
          const existing = map[key];
          if (isWorking) {
            // Working weekend/holiday — treat as a normal working day.
            // Keep any attendance record already present; otherwise just
            // annotate the holiday name without a holiday style.
            if (existing) {
              existing.holidayName = h.name;
            } else {
              map[key] = { date: key, holidayName: h.name, isWorkingHoliday: true };
            }
          } else {
            // Non-working holiday — apply holiday style + name.
            // Don't clobber an existing attendance record's status.
            if (existing) {
              existing.holidayName = h.name;
            } else {
              map[key] = { date: key, status: "HOLIDAY", holidayName: h.name };
            }
          }
        });
      } catch {
        // holiday route unavailable — skip overlay silently
      }

      // Approved-leave overlay — mark ON_LEAVE days from the monthly
      // response so leave days show on the calendar even with no
      // attendance row. Half-day leaves annotate without hiding work.
      try {
        const leaves = Array.isArray(data) ? [] : (data.leaves || []);
        const monthPrefix = `${year}-${String(month).padStart(2, "0")}`;
        leaves.forEach((lv) => {
          const from = lv.from_date || lv.fromDate;
          const to = lv.to_date || lv.toDate || from;
          if (!from) return;
          const isHalf = (lv.is_half_day ?? lv.isHalfDay) === true;
          // Walk each date in the leave range.
          let d = new Date(from + "T00:00:00");
          const end = new Date(to + "T00:00:00");
          while (d <= end) {
            const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
            if (key.slice(0, 7) === monthPrefix) {
              const existing = map[key];
              if (existing && (existing.check_in || existing.checkInTime)) {
                // Worked that day (e.g. half-day leave) — keep status,
                // annotate the leave type.
                existing.leaveType = lv.leave_type || lv.leaveType;
                existing.halfDayLeave = isHalf;
              } else {
                map[key] = {
                  date: key,
                  status: "ON_LEAVE",
                  leaveType: lv.leave_type || lv.leaveType,
                  halfDayLeave: isHalf,
                };
              }
            }
            d.setDate(d.getDate() + 1);
          }
        });
      } catch {
        // ignore leave overlay errors
      }

      setDays(map);
    } catch (err) {
      setError(err.message || "Could not load calendar.");
    } finally {
      setLoading(false);
    }
  }, [month, year]);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  // tell parent which month is shown so MonthlyStats stays in sync
  useEffect(() => { onPeriodChange && onPeriodChange({ month, year }); }, [month, year]); // eslint-disable-line

  const daysInMonth = new Date(year, month, 0).getDate();
  const firstDay = new Date(year, month - 1, 1).getDay(); // 0=Sun

  const prevMonth = () => {
    if (month === 1) { setMonth(12); setYear(year - 1); }
    else setMonth(month - 1);
    setPopup(null);
  };
  const nextMonth = () => {
    if (month === 12) { setMonth(1); setYear(year + 1); }
    else setMonth(month + 1);
    setPopup(null);
  };
  const goToday = () => {
    const t = new Date();
    setMonth(t.getMonth() + 1);
    setYear(t.getFullYear());
    setPopup(null);
  };
  const isCurrentMonth = month === (now.getMonth() + 1) && year === now.getFullYear();

  const dateKey = (d) => `${year}-${String(month).padStart(2, "0")}-${String(d).padStart(2, "0")}`;

  const todayNum = now.getDate();
  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(<div key={`e${i}`} className="atl-day atl-day--empty" />);
  for (let d = 1; d <= daysInMonth; d++) {
    const key = dateKey(d);
    const info = days[key];
    // Weekday for this cell (0=Sun … 6=Sat) — used for weekend styling.
    const dow = new Date(year, month - 1, d).getDay();
    const isWeekend = dow === 0 || dow === 6;
    const statusCls =
      info && STATUS_CLASS[(info.status || "").toUpperCase()]
        ? STATUS_CLASS[info.status.toUpperCase()]
        : "";
    // Attendance status (or holiday overlay) wins; otherwise fall back to the
    // weekend style for Sat/Sun cells that have no record. A working-weekend
    // holiday (isWorkingHoliday) should NOT get the weekend style.
    const cls =
      statusCls ||
      (isWeekend && !(info && info.isWorkingHoliday) ? STATUS_CLASS.WEEKEND : "");
    const isToday = isCurrentMonth && d === todayNum;
    // Prefer the holiday name in the tooltip when present.
    const title = info
      ? (info.holidayName || info.status || "")
      : (isWeekend ? "Weekend" : "");
    // Short label shown inside the cell: status label if present, else "Off"
    // for a plain weekend day.
    const cellLabel =
      info && STATUS_LABEL[(info.status || "").toUpperCase()]
        ? STATUS_LABEL[info.status.toUpperCase()]
        : (!info && isWeekend ? STATUS_LABEL.WEEKEND : "");
    cells.push(
      <div
        key={d}
        className={`atl-day ${cls} ${isToday ? "atl-day--today" : ""}`}
        onClick={() => info && setPopup({ day: d, info })}
        title={title}
      >
        <span className="atl-day__num">{d}</span>
        {cellLabel && <span className="atl-day__label">{cellLabel}</span>}
      </div>
    );
  }

  return (
    <div className="atl-card atl-calendar">
      <div className="atl-calendar__head">
        <button className="atl-nav" onClick={prevMonth} aria-label="Previous month">‹</button>
        <h3>{MONTHS[month - 1]} {year}</h3>
        <button className="atl-nav" onClick={nextMonth} aria-label="Next month">›</button>
        {!isCurrentMonth && (
          <button className="atl-today-btn" onClick={goToday}>Today</button>
        )}
      </div>

      {error && <div className="atl-error">{error}</div>}

      <div className="atl-calendar__weekdays">
        {["Sun","Mon","Tue","Wed","Thu","Fri","Sat"].map((w) => <span key={w}>{w}</span>)}
      </div>

      {loading ? (
        <div className="atl-skeleton atl-skeleton--tall" />
      ) : (
        <div className="atl-calendar__grid">{cells}</div>
      )}

      {/* Legend */}
      <div className="atl-legend">
        <span><i className="atl-dot atl-day--present" /> Present</span>
        <span><i className="atl-dot atl-day--late" /> Late</span>
        <span><i className="atl-dot atl-day--absent" /> Absent</span>
        <span><i className="atl-dot atl-day--half" /> Half-day</span>
        <span><i className="atl-dot atl-day--leave" /> Leave</span>
        <span><i className="atl-dot atl-day--holiday" /> Holiday</span>
        <span><i className="atl-dot atl-day--weekend" /> Weekend</span>
      </div>

      {/* Mini popup banner */}
      {popup && (
        <div className="atl-popup" onClick={() => setPopup(null)}>
          <div className="atl-popup__box" onClick={(e) => e.stopPropagation()}>
            <div className="atl-popup__head">
              <strong>{MONTHS[month - 1]} {popup.day}, {year}</strong>
              <button onClick={() => setPopup(null)}>×</button>
            </div>
            {(popup.info.holidayName) ? (
              <div className="atl-popup__row"><span>Holiday</span><b>{popup.info.holidayName}</b></div>
            ) : null}
            <div className="atl-popup__row"><span>Status</span><b>{popup.info.status || "—"}</b></div>
            <div className="atl-popup__row"><span>Check In</span><b>{fmt(popup.info.checkInTime || popup.info.check_in || popup.info.check_in_time)}</b></div>
            <div className="atl-popup__row"><span>Check Out</span><b>{fmt(popup.info.checkOutTime || popup.info.check_out || popup.info.check_out_time)}</b></div>
            <div className="atl-popup__row"><span>Hours</span><b>{popup.info.totalHours || popup.info.total_hours || "—"}</b></div>
            {(popup.info.lateBy || popup.info.late_by) ? (
              <div className="atl-popup__row"><span>Late by</span><b>{popup.info.lateBy || popup.info.late_by} min</b></div>
            ) : null}
            {(popup.info.notes || popup.info.note) ? (
              <div className="atl-popup__row"><span>Notes</span><b>{popup.info.notes || popup.info.note}</b></div>
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
}

function fmt(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleTimeString("en-IN", { timeZone: "Asia/Kolkata", hour: "2-digit", minute: "2-digit" });
}

export default AttendanceCalendar;
